import React, { memo, ReactNode, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { ThemeOverride } from '@plasma/ui.utils.types';
import useAssociatedPersonnelHook from './associated-personnel.hook';
import * as styled from './associated-personnel.styles';
import themes from './themes';
import Select from '@plasma/ui.input.select';
const { Option } = Select;
import Modal from '@plasma/ui.layout.modal/dist/modal';
import Switch from '@plasma/ui.input.switch';
import Input from '@plasma/ui.input.input';
import LoadingComponent from '../../components/Loding-component/LoadingComponent';
import TopBarThemed from '../../components/top-bar-themed';
import {
  ESCALATION_LEVEL_LABEL_KEYS,
  EscalationLevel,
  isInternalPersonnel,
  PERSONNEL_TYPE_LABEL_KEYS,
  PersonnelType,
} from '../../constants/personnel';

export interface AssociatedPersonnelProps {
  children?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

type Props = AssociatedPersonnelProps & ThemeOverride;

const ASSOCIATE_DROPZONE_ID = 'associate-dropzone';
const DEASSOCIATE_DROPZONE_ID = 'deassociate-dropzone';
const SOURCE_DRAG_PREFIX = 'source-';
const ASSOC_DRAG_PREFIX = 'assoc-';

function personnelTypeTone(flagInternal: number): 'external' | 'region' | 'coc' {
  if (flagInternal === PersonnelType.InternalCoc) return 'coc';
  if (flagInternal === PersonnelType.InternalRegion) return 'region';
  return 'external';
}

function escalationTone(level: EscalationLevel): 'low' | 'medium' | 'high' {
  if (level === EscalationLevel.High) return 'high';
  if (level === EscalationLevel.Medium) return 'medium';
  return 'low';
}

interface DraggableItemProps {
  id: string;
  children: (isDragging: boolean) => ReactNode;
  onClick?: () => void;
}

function DraggableItem({ id, children, onClick }: DraggableItemProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = transform ? { transform: CSS.Translate.toString(transform) } : undefined;

  return (
    <styled.DraggableWrapper ref={setNodeRef} style={style} $isDragging={isDragging} {...listeners} {...attributes} onClick={onClick}>
      {children(isDragging)}
    </styled.DraggableWrapper>
  );
}

function AssociateDropZone({ children }: { children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: ASSOCIATE_DROPZONE_ID });
  return (
    <styled.DropZone ref={setNodeRef} $isOver={isOver}>
      {children}
    </styled.DropZone>
  );
}

function RemoveDropZone({ label }: { label: string }) {
  const { setNodeRef, isOver } = useDroppable({ id: DEASSOCIATE_DROPZONE_ID });
  return (
    <styled.RemoveDropZone ref={setNodeRef} $isOver={isOver}>
      {label}
    </styled.RemoveDropZone>
  );
}

function AssociatedPersonnel(props: Props) {
  const { children, className, style, size, themeMode } = props;
  const {
    t,
    personnel,
    isMouseEntered,
    showModal,
    HandleModalCancel,
    getAssocite,
    remarks,
    changeRemarks,
    HandleModalOk,
    handleDropToAssociate,
    handleDropToDeassociate,
    escalationOption,
    escalationValue,
    sendMail,
    handleEscalationLevelChange,
    handleSendMail,
  } = useAssociatedPersonnelHook(props);

  const [draggedLabel, setDraggedLabel] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Keep the source lists and the "Personnel with" list mutually exclusive - once someone is
  // associated with this company, dragging them again would just fail (already mapped), so they
  // shouldn't still show up as a candidate.
  const associatedPersonnelIds = new Set(getAssocite.map((person) => person.personnelId));
  const availablePersonnel = personnel.filter((item) => !associatedPersonnelIds.has(item.id));
  const internalPersonnel = availablePersonnel.filter((item) => isInternalPersonnel(item.flagInternal));
  const externalPersonnel = availablePersonnel.filter((item) => !isInternalPersonnel(item.flagInternal));

  const findPersonnelById = (id: string) => personnel.find((item) => item.id === id);
  const findAssociatedById = (id: string) => getAssocite.find((item) => item.personnelId === id);

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith(SOURCE_DRAG_PREFIX)) {
      setDraggedLabel(findPersonnelById(id.slice(SOURCE_DRAG_PREFIX.length))?.personnelName ?? null);
    } else if (id.startsWith(ASSOC_DRAG_PREFIX)) {
      setDraggedLabel(findAssociatedById(id.slice(ASSOC_DRAG_PREFIX.length))?.personnelName ?? null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedLabel(null);
    const { active: activeItem, over } = event;
    const activeId = String(activeItem.id);

    if (activeId.startsWith(SOURCE_DRAG_PREFIX)) {
      if (over?.id === ASSOCIATE_DROPZONE_ID) {
        handleDropToAssociate(activeId.slice(SOURCE_DRAG_PREFIX.length));
      }
      return;
    }

    if (activeId.startsWith(ASSOC_DRAG_PREFIX)) {
      // Picking a person up from the Associated list and letting go anywhere except back onto
      // that same list - blank space, or the explicit Remove zone - de-associates them. Dropping
      // back onto the Associated panel itself is a no-op (put it back where it came from).
      if (over?.id === ASSOCIATE_DROPZONE_ID) {
        return;
      }
      const person = findAssociatedById(activeId.slice(ASSOC_DRAG_PREFIX.length));
      if (person) handleDropToDeassociate(person);
    }
  };

  return (
    <ThemeProvider themes={themes} selectedSize={size} selectedMode={themeMode}>
      <styled.AssociatedPersonnel className={`associated-personnel ${className ?? ''}`} style={style}>
        {personnel ? (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <TopBarThemed topHeading={t('associate-personnel')} />

            <styled.PageContent>
              <styled.PanelsRow>
                <styled.Panel>
                  <styled.PanelHeader>
                    <styled.PersonnelCategoryTitle>{t('external-personnel')}</styled.PersonnelCategoryTitle>
                  </styled.PanelHeader>
                  <styled.PersonnelList>
                    {externalPersonnel.map((item) => (
                      // External (customer) contacts can be associated too - it's how they opt
                      // in/out of RFQ emails (FlagEmail) for their own company. The backend only
                      // allows mapping them to the company they already belong to, not any other.
                      <DraggableItem key={item.id} id={`${SOURCE_DRAG_PREFIX}${item.id}`}>
                        {() => (
                          <styled.PersonnelItem isMouseEntered={isMouseEntered} isClicked={false}>
                            <styled.PersonnelRow>
                              <styled.PersonnelItemElementBold>{item.personnelName}, </styled.PersonnelItemElementBold>
                              <styled.PersonalItemElement>{item.personnelEmail}</styled.PersonalItemElement>
                              <styled.Pill $tone={personnelTypeTone(item.flagInternal)}>
                                {t(PERSONNEL_TYPE_LABEL_KEYS[item.flagInternal as PersonnelType])}
                              </styled.Pill>
                            </styled.PersonnelRow>
                          </styled.PersonnelItem>
                        )}
                      </DraggableItem>
                    ))}
                  </styled.PersonnelList>
                </styled.Panel>

                <styled.Panel>
                  <styled.PanelHeader>
                    <styled.PersonnelCategoryTitle>{t('internal-personnel')}</styled.PersonnelCategoryTitle>
                  </styled.PanelHeader>
                  <styled.PersonnelList>
                    {internalPersonnel.map((item) => (
                      <DraggableItem key={item.id} id={`${SOURCE_DRAG_PREFIX}${item.id}`}>
                        {() => (
                          <styled.PersonnelItem isMouseEntered={isMouseEntered} isClicked={false}>
                            <styled.PersonnelRow>
                              <styled.PersonnelItemElementBold>{item.personnelName}, </styled.PersonnelItemElementBold>
                              <styled.PersonalItemElement>{item.personnelEmail}</styled.PersonalItemElement>
                              <styled.Pill $tone={personnelTypeTone(item.flagInternal)}>
                                {t(PERSONNEL_TYPE_LABEL_KEYS[item.flagInternal as PersonnelType])}
                              </styled.Pill>
                              {item.entityName ? (
                                <styled.PersonalItemElement>({item.entityName})</styled.PersonalItemElement>
                              ) : null}
                            </styled.PersonnelRow>
                          </styled.PersonnelItem>
                        )}
                      </DraggableItem>
                    ))}
                  </styled.PersonnelList>
                </styled.Panel>

                <styled.Panel>
                  <styled.PanelHeader>
                    <styled.PersonnelCategoryTitle>{t('actions')}</styled.PersonnelCategoryTitle>
                  </styled.PanelHeader>
                  <styled.ActionsPanelBody>
                    <styled.PersonalItemElement>{t('drag-to-associate-hint')}</styled.PersonalItemElement>
                    <RemoveDropZone label={t('drag-here-to-remove')} />
                  </styled.ActionsPanelBody>
                </styled.Panel>
              </styled.PanelsRow>

              <styled.Panel>
                <styled.PanelHeader>
                  <styled.PersonnelCategoryTitle>{t('personnel-with')}</styled.PersonnelCategoryTitle>
                </styled.PanelHeader>
                <AssociateDropZone>
                  <styled.PersonnelList>
                    {getAssocite && getAssocite.length ? (
                      getAssocite.map((person) => (
                        <DraggableItem key={person.personnelId} id={`${ASSOC_DRAG_PREFIX}${person.personnelId}`}>
                          {() => (
                            <styled.AssociatedPersonnelItem
                              isMouseEntered={isMouseEntered}
                              isClicked={false}
                              flaggedPerson={person.flagEmail}
                            >
                              <styled.PersonnelRow>
                                <styled.PersonnelItemElementBold>{person.personnelName},</styled.PersonnelItemElementBold>
                                <styled.PersonalItemElement>{person.personnelEmail},</styled.PersonalItemElement>
                                <styled.PersonnelItemElementBold>{person.designation},</styled.PersonnelItemElementBold>
                                <styled.PersonalItemElement>{person.department}</styled.PersonalItemElement>
                                {person.entityName ? (
                                  <styled.PersonalItemElement>({person.entityName})</styled.PersonalItemElement>
                                ) : null}
                                <styled.Pill $tone={escalationTone(person.escalationLevel)}>
                                  {t(ESCALATION_LEVEL_LABEL_KEYS[person.escalationLevel])}
                                </styled.Pill>
                              </styled.PersonnelRow>
                            </styled.AssociatedPersonnelItem>
                          )}
                        </DraggableItem>
                      ))
                    ) : (
                      <></>
                    )}
                  </styled.PersonnelList>
                </AssociateDropZone>
              </styled.Panel>
            </styled.PageContent>

            <Modal
              title={t('associate-personnel-modal-title')}
              width={'30vw'}
              okText={t('save')}
              cancelText={t('cancel')}
              visible={showModal}
              onCancel={HandleModalCancel}
              onOk={HandleModalOk}
              destroyOnClose
            >
              <styled.ModalContainer>
                <styled.FormGrid $columns={2}>
                  <styled.FormGroup>
                    <styled.Label>{t('send-email')}?</styled.Label>
                    <styled.InlineFormGroup>
                      <Switch checked={sendMail} onChange={handleSendMail} />
                    </styled.InlineFormGroup>
                  </styled.FormGroup>
                  <styled.FormGroup>
                    <styled.Label>{t('escalation_level')}</styled.Label>
                    <Select value={escalationValue} onChange={handleEscalationLevelChange} defaultActiveFirstOption>
                      {escalationOption.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.display}
                        </Option>
                      ))}
                    </Select>
                  </styled.FormGroup>
                </styled.FormGrid>

                <styled.FormGrid $columns={1}>
                  <styled.FormGroup>
                    <styled.Label>{t('remarks')}</styled.Label>
                    <Input type="text" id="remarks" value={remarks} onChange={changeRemarks} />
                  </styled.FormGroup>
                </styled.FormGrid>
              </styled.ModalContainer>
            </Modal>

            <DragOverlay>{draggedLabel ? <styled.PersonnelItemElementBold>{draggedLabel}</styled.PersonnelItemElementBold> : null}</DragOverlay>
          </DndContext>
        ) : (
          <LoadingComponent />
        )}
        {children}
      </styled.AssociatedPersonnel>
    </ThemeProvider>
  );
}

export default memo(AssociatedPersonnel) as unknown as typeof AssociatedPersonnel;
